import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import JSZip from 'jszip';
import './DeckVisualizer.css';

// Import sidebar components
import { 
  DeckStats, 
  CardSearch, 
  Recommendations, 
  DrawSimulator, 
  PreloadDecks 
} from './sidebar';

// Import constants and utilities
import { PRELOAD_DECKS, CHART_OPTIONS } from '../constants';
import { 
  getMaxCopiesAllowed, 
  getProxyImageUrl, 
  validateDeck, 
  getLevelChartData, 
  getCharacterChartData,
  sortCardsForDisplay,
  normalizeCard,
  shuffleArray,
  expandDeckByCount
} from '../utils';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

function DeckVisualizer() {
  const [cards, setCards] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [exportCode, setExportCode] = useState('');
  const [showExportCode, setShowExportCode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [searchPage, setSearchPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [totalLoadedCards, setTotalLoadedCards] = useState(0);
  const [collapseStats, setCollapseStats] = useState(true);
  const [collapseSearch, setCollapseSearch] = useState(false);
  const [collapsePreloadDecks, setCollapsePreloadDecks] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [collapseRecommendations, setCollapseRecommendations] = useState(false);
  const [collapseFilters, setCollapseFilters] = useState(true);
  const [selectedCardTypes, setSelectedCardTypes] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [collapseDrawSimulator, setCollapseDrawSimulator] = useState(false);
  const [drawnCards, setDrawnCards] = useState([]);
  const [remainingDeck, setRemainingDeck] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mulliganCount, setMulliganCount] = useState(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track mobile viewport to disable TTS export on small screens
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Memoized deck validation
  const deckValidation = useMemo(() => validateDeck(cards), [cards]);
  
  // Memoized chart data getters
  const levelChartData = useCallback(() => getLevelChartData(deckValidation.stats), [deckValidation.stats]);
  const characterChartData = useCallback(() => getCharacterChartData(deckValidation.stats), [deckValidation.stats]);

  // Deck validation and statistics
  // (Moved to utils/deckUtils.js)

  const clearCards = () => {
    setCards([]);
    setShowClearConfirm(false);
  };

  const exportDeck = () => {
    if (cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    try {
      const deckData = {
        version: 2,
        timestamp: new Date().toISOString(),
        cards: cards.map(card => ({
          id: card.id,
          count: card.count
        }))
      };
      
      const deckString = JSON.stringify(deckData);
      const encodedDeck = btoa(unescape(encodeURIComponent(deckString)));
      setExportCode(encodedDeck);
      setShowExportCode(true);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export deck');
    }
  };

  const importDeck = async () => {
    if (!importCode.trim()) {
      alert('Please paste a deck code first!');
      return;
    }

    try {
      const deckString = decodeURIComponent(escape(atob(importCode.trim())));
      const deckData = JSON.parse(deckString);
      
      // Handle both v1 (legacy) and v2 (new) formats
      let importedCards = [];
      
      if (deckData.version === 2 && deckData.cards) {
        // New format with card IDs - fetch full metadata from API
        const cardPromises = deckData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}`)
            .then(res => res.json())
            .then(card => {
              // Normalize card data to ensure feature field is preserved
              const normalized = normalizeCard(card);
              return {
                ...normalized,
                count: entry.count || 1
              };
            })
            .catch(error => {
              console.error(`Failed to fetch card ${entry.id}:`, error);
              return null;
            })
        );
        
        const fetchedCards = await Promise.all(cardPromises);
        importedCards = fetchedCards.filter(Boolean);
      } else if (Array.isArray(deckData)) {
        // Legacy format - array of entries
        importedCards = deckData.map(entry => {
          if (entry.id && entry.name) {
            return entry;
          } else if (entry.imageUrl) {
            return {
              id: `legacy-${Date.now()}`,
              imageUrl: entry.imageUrl,
              count: entry.count || 1,
              name: 'Unknown Card'
            };
          }
          return null;
        }).filter(Boolean);
      }

      setCards(importedCards);
      setShowImport(false);
      setImportCode('');
    } catch (error) {
      console.error('Import error:', error);
      alert('Invalid deck code! Please check and try again.');
    }
  };

  const copyExport = () => {
    navigator.clipboard.writeText(exportCode).then(() => {
      const btn = document.getElementById('copyBtn');
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.textContent = 'Copy Code';
      }, 2000);
    });
  };

  const exportToTTS = async () => {
    if (cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    const totalCards = cards.reduce((sum, c) => sum + (parseInt(c.count) || 1), 0);
    if (totalCards !== 50) {
      alert(`Deck must have exactly 50 cards (current: ${totalCards}).`);
      return;
    }

    const deckNameInput = prompt('Name this deck for TTS export (used for file names):', 'Ultraman Deck');
    if (!deckNameInput) {
      return;
    }
    const safeDeckName = deckNameInput.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'deck';

    try {
      // Use fixed back URL for all cards
      const BACK_URL = "https://steamusercontent-a.akamaihd.net/ugc/16420027240527538/483413ACD3746E956A903173304A14D98BA1A95A/";
      
      // Build CustomDeck, DeckIDs, and ContainedObjects using full-resolution TTS images
      const customDeck = {};
      const deckIDs = [];
      const containedObjects = [];
      let deckIdCounter = 1;

      cards.forEach(card => {
        const count = parseInt(card.count) || 1;
        const faceUrl = card.images?.tts?.full || card.images?.tts?.thumbnail || card.images?.blob?.full || card.images?.blob?.thumbnail || card.images?.original?.full || card.images?.original?.thumbnail;
        
        if (!faceUrl) {
          console.warn(`No image found for card: ${card.name}`);
          return;
        }

        const deckKey = deckIdCounter.toString();
        const deckEntry = {
          "FaceURL": faceUrl,
          "BackURL": BACK_URL,
          "NumWidth": 1,
          "NumHeight": 1,
          "BackIsHidden": true,
          "UniqueBack": false,
          "Type": 0
        };

        customDeck[deckKey] = deckEntry;

        for (let i = 0; i < count; i++) {
          const cardId = deckIdCounter * 100 + i;
          deckIDs.push(cardId);
          containedObjects.push({
            "GUID": Math.random().toString(16).slice(2, 8),
            "Name": "Card",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.69510412,
              "posZ": 13.9254141,
              "rotX": -3.36123918e-07,
              "rotY": 180.015976,
              "rotZ": 2.93983561e-07,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": `${card.name || 'Card'}${card.cardNumber ? ` - ${card.cardNumber}` : card.number ? ` - ${card.number}` : ''}`,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "Tags": [
              `Card Type: ${card.type || ''}`,
              `Character Name: ${card.characterName || ''}`,
              `Level: ${card.level || ''}`,
              "limit: ",
              `Rarity: ${card.rarity || ''}`,
              `Type: ${card.subtype || ''}`
            ],
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": true,
            "CardID": cardId,
            "SidewaysCard": false,
            "CustomDeck": {
              [deckKey]: deckEntry
            },
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": ""
          });
        }

        deckIdCounter++;
      });

      // Build TTS JSON structure
      const ttsData = {
        "SaveName": "",
        "Date": "",
        "VersionNumber": "",
        "GameMode": "",
        "GameType": "",
        "GameComplexity": "",
        "Tags": [],
        "Gravity": 0.5,
        "PlayArea": 0.5,
        "Table": "",
        "Sky": "",
        "Note": "",
        "TabStates": {},
        "LuaScript": "",
        "LuaScriptState": "",
        "XmlUI": "",
        "ObjectStates": [
          {
            "GUID": "4a0f84",
            "Name": "Deck",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.71205807,
              "posZ": 13.9254141,
              "rotX": -5.1007197e-07,
              "rotY": 180.015976,
              "rotZ": 1.57628949e-06,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": deckNameInput,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": false,
            "SidewaysCard": false,
            "DeckIDs": deckIDs,
            "CustomDeck": customDeck,
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": "",
            "ContainedObjects": containedObjects
          }
        ]
      };

      const zip = new JSZip();
      zip.file(`${safeDeckName}.json`, JSON.stringify(ttsData, null, 2));

      const firstImageUrl = containedObjects[0]?.CustomDeck?.[Object.keys(containedObjects[0]?.CustomDeck || {})[0]]?.FaceURL || cards[0]?.images?.tts?.full;
      if (firstImageUrl) {
        try {
          const response = await fetch(firstImageUrl);
          const arrayBuffer = await response.arrayBuffer();
          zip.file(`${safeDeckName}.png`, arrayBuffer);
        } catch (imgErr) {
          console.warn('Failed to fetch first image for zip PNG:', imgErr);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeDeckName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('TTS export error:', error);
      alert('Failed to export deck to TTS format: ' + error.message);
    }
  };

  const exportDeckImage = async () => {
    if (cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    try {
      // Build card list with IDs and counts for the server
      const cardList = cards.map(card => ({
        cardId: card.cardNumber || card.number,
        count: parseInt(card.count) || 1
      }));

      const res = await fetch('/api/export/deck-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cards: cardList })
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Export failed' }));
        throw new Error(error.error || 'Export failed');
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ultraman-deck.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Deck image export error:', error);
      alert('Failed to generate deck image: ' + error.message);
    }
  };

  const searchCards = async (page = 0) => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter a card name');
      return;
    }

    setSearchLoading(true);
    if (page === 0) {
      setSearchError('');
      setSearchResults([]);
      setTotalLoadedCards(0);
    }

    try {
      const offset = page === 0 ? 0 : totalLoadedCards;
      const response = await fetch('/api/search-cards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: searchQuery, 
          offset, 
          limit: 20,
          cardTypes: selectedCardTypes.length > 0 ? selectedCardTypes : undefined,
          characters: selectedCharacters.length > 0 ? selectedCharacters : undefined,
          levels: selectedLevels.length > 0 ? selectedLevels : undefined
        }),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newResults = page === 0 ? data.results : [...searchResults, ...(data.results || [])];
      setSearchResults(newResults);
      setSearchPage(page);
      setTotalLoadedCards(page === 0 ? (data.results || []).length : totalLoadedCards + (data.results || []).length);
      setHasMoreResults((data.results || []).length === 20);

      if (newResults.length === 0 && page === 0) {
        setSearchError('No cards found matching your search');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search cards. Make sure the backend is running.');
    } finally {
      setSearchLoading(false);
    }
  };

  const loadMoreCards = () => {
    if (!searchLoading && hasMoreResults) {
      searchCards(searchPage + 1);
    }
  };

  const handleSearchResultsScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Load more when user scrolls within 200px of bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMoreCards();
    }
  };

  const fetchRecommendations = async (deckCards) => {
    if (deckCards.length === 0) {
      setRecommendations([]);
      return;
    }

    try {
      setRecommendationsLoading(true);
      const response = await fetch('/api/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deckCards, limit: 10 }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error('Recommendations error:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSelectedCardTypes([]);
    setSelectedCharacters([]);
    setSelectedLevels([]);
  };

  const drawInitialHand = () => {
    // Create and shuffle deck
    const fullDeck = shuffleArray(expandDeckByCount(cards));

    // Draw first 6 cards
    const hand = fullDeck.slice(0, 6);
    const remaining = fullDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setHasDrawn(true);
  };

  const mulligan = () => {
    if (mulliganCount >= 1) {
      alert('You can only mulligan once!');
      return;
    }
    
    if (remainingDeck.length < 6) {
      alert('Not enough cards left in deck to mulligan!');
      return;
    }

    const hand = remainingDeck.slice(0, 6);
    const remaining = remainingDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setMulliganCount(mulliganCount + 1);
  };

  const resetDraw = () => {
    setMulliganCount(0);
    drawInitialHand();
  };

  const updateCardCount = (cardId, delta) => {
    const cardIndex = cards.findIndex(c => (c.cardNumber || c.number || c.id) === cardId);
    if (cardIndex === -1) return;
    
    const newCards = [...cards];
    const currentCount = parseInt(newCards[cardIndex].count) || 1;
    const newCount = currentCount + delta;
    const cardNumber = newCards[cardIndex].cardNumber || newCards[cardIndex].number;
    const maxAllowed = getMaxCopiesAllowed(cardNumber);
    
    // Allow removal at 0, enforce max based on custom rules
    if (newCount < 0) return;
    if (newCount > maxAllowed) return;
    
    if (newCount === 0) {
      // Remove card from deck
      newCards.splice(cardIndex, 1);
    } else {
      newCards[cardIndex].count = newCount;
    }
    
    setCards(newCards);
  };

  const addCardFromSearch = (card) => {
    // Check if card already exists in deck by cardNumber (unique identifier)
    const uniqueCardNumber = card.number;
    const existingCardIndex = cards.findIndex(c => (c.cardNumber || c.number) === uniqueCardNumber);
    const maxAllowed = getMaxCopiesAllowed(uniqueCardNumber);
    
    let updatedCards;
    if (existingCardIndex !== -1) {
      // Card already exists - increment count (if not already at max allowed)
      updatedCards = [...cards];
      if (updatedCards[existingCardIndex].count < maxAllowed) {
        updatedCards[existingCardIndex].count += 1;
      }
    } else {
      // New card - add it with normalized data
      const newCard = normalizeCard(card);
      updatedCards = [...cards, newCard];
    }

    setCards(updatedCards);
    // Fetch recommendations based on updated deck
    fetchRecommendations(updatedCards);
  };

  // Chart data is generated via utility functions - see levelChartData and characterChartData callbacks

  return (
    <div className={`container${sidebarExpanded ? ' sidebar-expanded' : ''}`}>
      <div className="header">
        <h1>Gardeno Games Deck Visualizer *BETA</h1>
        <p>Ultraman Card Game Deck Builder</p>
      </div>

      <div className="main-wrapper">
        {/* Main Content Area (Left) */}
        <div className="main-content">
          <div className="controls">
            <h3>Deck Management</h3>
            <button onClick={() => setShowClearConfirm(true)}>Clear All</button>
            <button onClick={exportDeck}>Export Deck Code</button>
            <button onClick={exportDeckImage}>Generate Deck Image</button>
            <button onClick={exportToTTS} disabled={isMobile}>Export to TTS</button>
            <button onClick={() => setShowImport(!showImport)}>Import Deck Code</button>
            {showExportCode && <button id="copyBtn" onClick={copyExport}>Copy Code</button>}

            {showClearConfirm && (
              <div className="confirm-dialog">
                <p>Are you sure you want to clear all cards?</p>
                <button onClick={clearCards}>Yes, Clear All</button>
                <button onClick={() => setShowClearConfirm(false)}>Cancel</button>
              </div>
            )}

            {showImport && (
              <div className="import-section">
                <textarea
                  value={importCode}
                  onChange={(e) => setImportCode(e.target.value)}
                  placeholder="Paste your deck code here..."
                />
                <button onClick={() => importDeck()}>Load Deck</button>
                <button onClick={() => setShowImport(false)}>Cancel</button>
              </div>
            )}

            {showExportCode && (
              <div className="export-code">
                <p>{exportCode}</p>
              </div>
            )}

            <div className="instructions">
              <strong>Instructions:</strong> Use the sidebar to search for cards by name and add them to your deck.<br />
              <br />
              <strong>Export:</strong> Creates a compressed code of your deck. <strong>Import:</strong> Paste the export code to restore your deck.<br />
              <br />
              <strong>Generate Image:</strong> Creates a visual representation of your deck.<br />
              <br />
              <strong>Export to TTS:</strong> Downloads a .zip file that can be imported into Tabletop Simulator. Upload the contents to your TTS saved objects folder. <br />
              Example: "\Documents\My Games\Tabletop Simulator\Saves\Saved Objects"
            </div>
          </div>

          <div className="card-grid" id="cardGrid">
            {sortCardsForDisplay(cards).map((card, index) => {
              const cardId = card.cardNumber || card.number || card.id;
              const imageUrl = getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full);
              const rarityText = typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '';
              const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
              const featureValue = card.feature || '';
              const isScene = featureValue === 'scene';
              const isHovered = hoveredCardIndex === cardId;
              
              return (
                <div 
                  key={cardId} 
                  className={`card-wrapper${isScene ? ' scene-card' : ''}`}
                  onMouseEnter={() => setHoveredCardIndex(cardId)}
                  onMouseLeave={() => setHoveredCardIndex(null)}
                >
                  {imageUrl && <img src={imageUrl} alt={card.name} className="card-image" />}
                  <div className="card-info">
                    <div className="card-name">{card.name}</div>
                    <div className="card-meta">{card.number}</div>
                    {rarityText && <div className="card-rarity">{rarityText}</div>}
                    {typeText && <div className="card-type">{typeText}</div>}
                  </div>
                  <div className="card-count">x{card.count}</div>
                  
                  {isHovered && (
                    <div className="card-overlay">
                      <div className="overlay-content">
                        <h4>{card.name}</h4>
                        {card.number && <p>{card.number}</p>}
                        <div className="overlay-controls">
                          <button 
                            className="count-btn decrease-btn"
                            onClick={() => updateCardCount(cardId, -1)}
                            title="Decrease count or remove"
                          >
                            −
                          </button>
                          <span className="count-display">x{card.count}</span>
                          <button 
                            className="count-btn increase-btn"
                            onClick={() => updateCardCount(cardId, 1)}
                            disabled={card.count >= getMaxCopiesAllowed(card.cardNumber || card.number)}
                            title="Increase count"
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="delete-card-btn"
                          onClick={() => updateCardCount(cardId, -card.count)}
                          title="Remove card from deck"
                        >
                          🗑 Remove Card
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search Sidebar (Right - Always Pinned) */}
      <div className={`sidebar-pinned${sidebarExpanded ? ' sidebar-expanded' : ''}`}>
        <div className="sidebar-header" onClick={() => setSidebarExpanded(!sidebarExpanded)} style={{ cursor: 'pointer' }}>
          <h3>🔍 Card Search</h3>
          <button 
            className="sidebar-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              setSidebarExpanded(!sidebarExpanded);
            }}
            title={sidebarExpanded ? "Collapse" : "Expand"}
          >
            {sidebarExpanded ? '✕' : '⌃'}
          </button>
        </div>

        <div className="sidebar-content">
          {/* Deck Stats Section */}
          <DeckStats
            isCollapsed={collapseStats}
            onToggle={() => setCollapseStats(!collapseStats)}
            deckValidation={deckValidation}
            getLevelChartData={levelChartData}
            getCharacterChartData={characterChartData}
            chartOptions={CHART_OPTIONS}
          />

          {/* Card Search Section */}
          <CardSearch
            isCollapsed={collapseSearch}
            onToggle={() => setCollapseSearch(!collapseSearch)}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearch={() => { setSearchPage(0); setTotalLoadedCards(0); searchCards(0); }}
            searchLoading={searchLoading}
            searchError={searchError}
            searchResults={searchResults}
            hasMoreResults={hasMoreResults}
            onResultsScroll={handleSearchResultsScroll}
            onAddCard={addCardFromSearch}
            getProxyImageUrl={getProxyImageUrl}
            sidebarExpanded={sidebarExpanded}
            collapseFilters={collapseFilters}
            onToggleFilters={() => setCollapseFilters(!collapseFilters)}
            selectedCardTypes={selectedCardTypes}
            onCardTypesChange={setSelectedCardTypes}
            selectedCharacters={selectedCharacters}
            onCharactersChange={setSelectedCharacters}
            selectedLevels={selectedLevels}
            onLevelsChange={setSelectedLevels}
            onClearFilters={clearAllFilters}
          />

          {/* Recommendations Section */}
          <Recommendations
            isCollapsed={collapseRecommendations}
            onToggle={() => setCollapseRecommendations(!collapseRecommendations)}
            recommendations={recommendations}
            loading={recommendationsLoading}
            onAddCard={addCardFromSearch}
            getProxyImageUrl={getProxyImageUrl}
            deckHasCards={cards.length > 0}
          />

          {/* Draw Simulator Section */}
          <DrawSimulator
            isCollapsed={collapseDrawSimulator}
            onToggle={() => setCollapseDrawSimulator(!collapseDrawSimulator)}
            drawnCards={drawnCards}
            remainingDeck={remainingDeck}
            hasDrawn={hasDrawn}
            mulliganCount={mulliganCount}
            deckHasCards={cards.length > 0}
            onDrawInitialHand={drawInitialHand}
            onMulligan={mulligan}
            onResetDraw={resetDraw}
            getProxyImageUrl={getProxyImageUrl}
          />

          {/* Preload Decks Section */}
          <PreloadDecks
            isCollapsed={collapsePreloadDecks}
            onToggle={() => setCollapsePreloadDecks(!collapsePreloadDecks)}
            preloadDecks={PRELOAD_DECKS}
            onLoadDeck={async (deck) => {
              if (deck.deckCode) {
                try {
                  const deckString = decodeURIComponent(escape(atob(deck.deckCode.trim())));
                  const deckData = JSON.parse(deckString);
                  
                  if (deckData.version === 2 && deckData.cards) {
                    const cardPromises = deckData.cards.map(entry =>
                      fetch(`/api/cards/${entry.id}`)
                        .then(res => res.json())
                        .then(card => {
                          const normalized = normalizeCard(card);
                          return { ...normalized, count: entry.count || 1 };
                        })
                        .catch(() => null)
                    );
                    const fetchedCards = await Promise.all(cardPromises);
                    setCards(fetchedCards.filter(Boolean));
                  }
                } catch (error) {
                  console.error('Failed to load deck:', error);
                  alert('Failed to load deck');
                }
              } else {
                alert('This deck is not yet available.');
              }
            }}
          />
        </div>
      </div>

      <button 
        className="sidebar-toggle-btn" 
        onClick={() => setSidebarExpanded(!sidebarExpanded)}
        title={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {sidebarExpanded ? '▶' : '◀'}
      </button>

      <div className="footer">
        <p>© 2025 Gardeno Games Deck Visualizer. Ultraman Card Game and the Ultraman IP are trademarks of Tsuburaya Productions.</p>
      </div>
    </div>
  );
}

export default DeckVisualizer;
